import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { FaBell, FaUserPlus, FaCommentDots, FaCode, FaBookOpen } from 'react-icons/fa';
import { API_BASE } from '../utils/apiEndpoints';
import './css/NotificationsPage.css';

interface NotificationItem {
  id: number;
  type: string;
  payload: {
    class_id?: number;
    class_name?: string;
    invite_code?: string;
    problem_id?: number;
    problem_title?: string;
    problem_title_i18n?: Record<string, string> | null;
    student_id?: number;
    student_name?: string;
    student_username?: string;
    lesson_title?: string;
    due_at?: string | null;
    change?: string;
    path?: string;
  } | null;
  read_at: string | null;
  createdAt: string;
}

function notificationProblemTitle(
  payload: NotificationItem['payload'],
  lang: string,
): string {
  const i18n = payload?.problem_title_i18n;
  if (i18n && typeof i18n === 'object') {
    const local = i18n[lang];
    if (local && String(local).trim()) return String(local);
  }
  return payload?.problem_title || '?';
}

function isSafeProblemPath(path: string | undefined): path is string {
  return typeof path === 'string'
    && path.startsWith('/problems/')
    && !path.startsWith('//')
    && !path.includes('://');
}

function isSafeClassPath(path: string | undefined): path is string {
  return typeof path === 'string'
    && /^\/class\/[1-9]\d*(?:\?tab=analytics(?:&student=\d+)?|\?lesson=\d+)?$/.test(path);
}

function classLessonNoticeText(
  n: NotificationItem,
  t: (key: string) => string,
): string {
  const cls = n.payload?.class_name || '?';
  const lesson = n.payload?.lesson_title || '?';
  const due = n.payload?.due_at;
  if (n.payload?.change === 'due') {
    if (due) {
      return t('notifications.classLessonDue')
        .replace('{class}', cls)
        .replace('{lesson}', lesson)
        .replace('{date}', due);
    }
    return t('notifications.classLessonDueCleared')
      .replace('{class}', cls)
      .replace('{lesson}', lesson);
  }
  if (due) {
    return t('notifications.classLessonAssignedDue')
      .replace('{class}', cls)
      .replace('{lesson}', lesson)
      .replace('{date}', due);
  }
  return t('notifications.classLessonAssigned')
    .replace('{class}', cls)
    .replace('{lesson}', lesson);
}

function NotificationsPage() {
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [list, setList] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchNotifications = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/notifications`, { credentials: 'include' });
      if (!res.ok) throw new Error('Ошибка загрузки');
      const data = await res.json();
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('notifications.loadError'));
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchNotifications();
  }, [isAuthenticated]);

  const markRead = async (id: number) => {
    try {
      await fetch(`${API_BASE}/api/notifications/${id}/read`, {
        method: 'PATCH',
        credentials: 'include',
      });
      setList((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n))
      );
    } catch {
      // ignore
    }
  };

  const handleNotificationClick = (n: NotificationItem) => {
    if (n.type === 'class_invite' && n.payload?.invite_code) {
      markRead(n.id);
      navigate(`/join-class?code=${encodeURIComponent(n.payload.invite_code)}`);
      return;
    }
    if (n.type === 'class_feedback') {
      const path = n.payload?.path;
      if (isSafeProblemPath(path)) {
        markRead(n.id);
        navigate(path);
        return;
      }
    }
    if (n.type === 'class_submission' || n.type === 'class_feedback_reply' || n.type === 'class_lesson') {
      const path = n.payload?.path;
      if (isSafeClassPath(path)) {
        markRead(n.id);
        navigate(path);
        return;
      }
    }
    markRead(n.id);
  };

  const formatDate = (s: string) => {
    try {
      const d = new Date(s);
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return s;
    }
  };

  if (authLoading) {
    return (
      <div className="notifications-page">
        <div className="notifications-loading">{t('notifications.loading')}</div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="notifications-container">
        <h1 className="notifications-title">
          <FaBell className="notifications-title-icon" />
          {t('notifications.title')}
        </h1>
        {error && <div className="notifications-error">{error}</div>}
        {loading ? (
          <div className="notifications-loading">{t('notifications.loading')}</div>
        ) : list.length === 0 ? (
          <div className="notifications-empty">{t('notifications.empty')}</div>
        ) : (
          <ul className="notifications-list">
            {list.map((n) => (
              <li
                key={n.id}
                className={`notifications-item ${n.read_at ? 'read' : 'unread'}`}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(n)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNotificationClick(n);
                  }
                }}
              >
                {n.type === 'class_invite' && (
                  <>
                    <FaUserPlus className="notifications-item-icon" />
                    <div className="notifications-item-body">
                      <span className="notifications-item-text">
                        {t('notifications.classInvite').replace('{name}', n.payload?.class_name || '?')}
                      </span>
                      <span className="notifications-item-date">{formatDate(n.createdAt)}</span>
                    </div>
                  </>
                )}
                {n.type === 'class_feedback' && (
                  <>
                    <FaCommentDots className="notifications-item-icon" />
                    <div className="notifications-item-body">
                      <span className="notifications-item-text">
                        {t('notifications.classFeedback')
                          .replace('{class}', n.payload?.class_name || '?')
                          .replace('{problem}', notificationProblemTitle(n.payload, currentLanguage))}
                      </span>
                      <span className="notifications-item-date">{formatDate(n.createdAt)}</span>
                    </div>
                  </>
                )}
                {n.type === 'class_submission' && (
                  <>
                    <FaCode className="notifications-item-icon" />
                    <div className="notifications-item-body">
                      <span className="notifications-item-text">
                        {t('notifications.classSubmission')
                          .replace('{student}', n.payload?.student_name || n.payload?.student_username || '?')
                          .replace('{problem}', notificationProblemTitle(n.payload, currentLanguage))
                          .replace('{class}', n.payload?.class_name || '?')}
                      </span>
                      <span className="notifications-item-date">{formatDate(n.createdAt)}</span>
                    </div>
                  </>
                )}
                {n.type === 'class_feedback_reply' && (
                  <>
                    <FaCommentDots className="notifications-item-icon" />
                    <div className="notifications-item-body">
                      <span className="notifications-item-text">
                        {t('notifications.classFeedbackReply')
                          .replace('{student}', n.payload?.student_name || n.payload?.student_username || '?')
                          .replace('{problem}', notificationProblemTitle(n.payload, currentLanguage))
                          .replace('{class}', n.payload?.class_name || '?')}
                      </span>
                      <span className="notifications-item-date">{formatDate(n.createdAt)}</span>
                    </div>
                  </>
                )}
                {n.type === 'class_lesson' && (
                  <>
                    <FaBookOpen className="notifications-item-icon" />
                    <div className="notifications-item-body">
                      <span className="notifications-item-text">{classLessonNoticeText(n, t)}</span>
                      <span className="notifications-item-date">{formatDate(n.createdAt)}</span>
                    </div>
                  </>
                )}
                {n.type !== 'class_invite' && n.type !== 'class_feedback' && n.type !== 'class_submission' && n.type !== 'class_feedback_reply' && n.type !== 'class_lesson' && (
                  <div className="notifications-item-body">
                    <span className="notifications-item-text">{n.type}</span>
                    <span className="notifications-item-date">{formatDate(n.createdAt)}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
